import React from "react";
import {
  Alert,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  ButtonProps,
  FormField,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsExportPreset } from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { presetScopeLabel } from "./omsPresetEditing";

export default function OMSPresetToolbar({
  state,
  applied,
  edited,
  disabled,
  saveDisabled = disabled,
  onApply,
  onSave,
  onSaveAsNew,
  onUpdate,
  onManage,
  saveRef,
  selectRef,
  actionsRef,
}: {
  state: OmsPresetsState;
  applied?: OmsExportPreset;
  edited: boolean;
  disabled: boolean;
  saveDisabled?: boolean;
  onApply: (presetId: string) => void;
  onSave: () => void;
  onSaveAsNew: () => void;
  onUpdate: () => void;
  onManage: () => void;
  saveRef: React.RefObject<ButtonProps.Ref>;
  selectRef: React.RefObject<SelectProps.Ref>;
  actionsRef: React.RefObject<ButtonDropdownProps.Ref>;
}) {
  return (
    <SpaceBetween size="s">
      <div className="oms-preset-toolbar">
        <FormField label="Saved preset (optional)">
          <Select
            ref={selectRef}
            ariaLabel="Saved preset"
            placeholder="Choose a preset"
            filteringType="auto"
            filteringPlaceholder="Search presets"
            filteringAriaLabel="Search presets"
            filteringClearAriaLabel="Clear preset search"
            empty="No saved presets for this product."
            noMatch="No matching presets."
            statusType={
              state.list.isLoading || state.read.isLoading
                ? "loading"
                : "finished"
            }
            loadingText={
              state.read.isLoading ? "Applying preset" : "Loading presets"
            }
            disabled={disabled || state.read.isLoading}
            selectedOption={
              applied
                ? {
                    value: applied.presetId,
                    label: `${applied.name}${edited ? " (edited)" : ""}`,
                    description: presetScopeLabel[applied.scope],
                  }
                : null
            }
            options={(state.list.data || []).map((preset) => ({
              value: preset.presetId,
              label: preset.name,
              description: presetScopeLabel[preset.scope],
            }))}
            onChange={({ detail }) =>
              onApply(detail.selectedOption.value as string)
            }
          />
        </FormField>
        <Button
          ref={saveRef}
          disabled={saveDisabled || state.read.isLoading}
          onClick={onSave}
        >
          Save preset
        </Button>
        <ButtonDropdown
          ref={actionsRef}
          ariaLabel="Preset actions"
          disabled={disabled || state.read.isLoading}
          items={[
            {
              id: "update",
              text: "Update preset",
              disabled: !applied || !edited,
            },
            { id: "new", text: "Save as new" },
            { id: "manage", text: "Manage presets" },
          ]}
          onItemClick={({ detail }) => {
            if (detail.id === "update") onUpdate();
            else if (detail.id === "manage") onManage();
            else onSaveAsNew();
          }}
        >
          More actions
        </ButtonDropdown>
      </div>
      {state.list.isError && (
        <Alert
          type="warning"
          header="Presets unavailable"
          action={
            <Button
              onClick={() => {
                state.list.refetch().catch(() => undefined);
              }}
            >
              Retry presets
            </Button>
          }
        >
          You can still configure and create an export.
        </Alert>
      )}
      {state.read.isError && (
        <Alert
          type="error"
          header="Could not apply preset"
          action={
            <Button
              onClick={() => {
                if (state.read.variables) onApply(state.read.variables);
              }}
            >
              Retry preset
            </Button>
          }
        >
          {presetErrorMessage(state.read.error)}
        </Alert>
      )}
    </SpaceBetween>
  );
}
